import { FILE_DATA_INCLUDE_QUERY } from '@swell/apps-sdk';
import generatePassword from 'generate-password';
import Cookies from 'universal-cookie';
import { pull, find, sortBy } from 'lodash';
import moment from 'moment';

import api from 'services/api';
import { COOKIE_MAX_AGE } from 'services/cookie';
import { normalizeUrl } from 'utils';
import { getThemeStorefrontApp } from 'utils/app';
import {
  populateMenuLookupValues,
  depopulateMenuLookupValues,
} from 'utils/content';

import { USER_FETCH } from './user';
import clientActions from './client';
import appActions from './apps';

const cookies = new Cookies();

const STOREFRONTS_FETCH = 'storefronts/fetch';
const STOREFRONTS_FETCH_PRIMARY = 'storefronts/fetchPrimary';
const STOREFRONTS_FETCH_PRIMARY_THEME_TEMPLATES =
  'storefronts/fetchPrimaryThemeTemplates';
const STOREFRONTS_FETCH_ALL = 'storefronts/fetchAll';
const STOREFRONTS_FETCH_CONFIG = 'storefronts/fetchConfig';
const STOREFRONTS_FETCH_THEME_CONFIG = 'storefronts/fetchThemeConfig';
const STOREFRONTS_CREATE_THEME_CONFIG = 'storefronts/createThemeConfig';
const STOREFRONTS_UPDATE_THEME_CONFIG = 'storefronts/updateThemeConfig';
const STOREFRONTS_DELETE_THEME_CONFIG = 'storefronts/deleteThemeConfig';
const STOREFRONTS_FETCH_THEME_DETAILS = 'storefronts/fetchThemeDetails';
const STOREFRONTS_FETCH_THEME_CONFIGS = 'storefronts/fetchThemeConfigs';
const STOREFRONTS_CREATE = 'storefronts/create';
const STOREFRONTS_UPDATE = 'storefronts/update';
const STOREFRONTS_UPDATE_CONFIG = 'storefronts/updateConfig';
const STOREFRONTS_UPDATE_VERSION = 'storefronts/updateVersion';
const STOREFRONTS_CANCEL_UPDATE = 'storefronts/cancelUpdate';
const STOREFRONTS_DELETE = 'storefronts/delete';
const STOREFRONTS_CREATE_API_KEY = 'storefronts/createAPIKey';
const STOREFRONTS_UPDATE_API_KEY = 'storefronts/updateAPIKey';
const STOREFRONTS_DELETE_API_KEY = 'storefronts/deleteAPIKey';
const STOREFRONTS_FETCH_DOMAINS = 'storefronts/fetchDomains';
const STOREFRONTS_CREATE_DOMAIN = 'storefronts/createDomain';
const STOREFRONTS_UPDATE_DOMAIN = 'storefronts/updateDomain';
const STOREFRONTS_VERIFY_DOMAIN = 'storefronts/verifyDomain';
const STOREFRONTS_DELETE_DOMAIN = 'storefronts/deleteDomain';
const STOREFRONTS_FETCH_ENTRI_TOKEN = 'storefronts/fetchEntriToken';
const STOREFRONTS_FETCH_THUMBNAILS = 'storefronts/fetchThumbnails';
const STOREFRONTS_FETCH_INFO = 'storefronts/fetchInfo';
const STOREFRONTS_POST_PASSWORD = 'storefronts/postPassword';
const STOREFRONTS_GET_TEST_CHECKOUT = 'storefronts/getTestCheckout';
const STOREFRONTS_START_PREVIEW = 'storefronts/startPreview';
const STOREFRONTS_LOADING = 'storefronts/loading';

const actions = {
  fetchPrimary() {
    return async (dispatch) => {
      const result = await dispatch({
        type: STOREFRONTS_FETCH_PRIMARY,
        payload: api.get('/storefront'),
      });

      dispatch(this.fetchThumbnails(result));

      if (result?.app_id && result?.theme_id) {
        await dispatch(this.fetchPrimaryThemeTemplates(result));
      }

      return result;
    };
  },

  loadPrimary() {
    return (dispatch, getState) => {
      const { storefronts } = getState();
      if (storefronts.fetchedPrimary) {
        return Promise.resolve(storefronts.primary);
      }
      return dispatch(this.fetchPrimary());
    };
  },

  fetch(id) {
    return async (dispatch) => {
      const result = await dispatch({
        type: STOREFRONTS_FETCH,
        payload: api.get(`/storefronts/${id}`),
      });

      dispatch(this.fetchThumbnails(result));
      return result;
    };
  },

  startPreview(id) {
    return {
      type: STOREFRONTS_START_PREVIEW,
      payload: api.post(`/storefronts/${id}/preview/start`),
    };
  },

  load(id) {
    return async (dispatch, getState) => {
      const { storefronts } = getState();
      if (storefronts.all?.results) {
        const storefront = storefronts.all.results.find(
          (storefront) => storefront.id === id,
        );
        if (storefront) {
          await dispatch({
            type: STOREFRONTS_FETCH,
            payload: storefront,
          });
          return Promise.resolve(storefront);
        }
      }
      return dispatch(this.fetch(id));
    };
  },

  fetchAll() {
    return async (dispatch) => {
      const result = await dispatch({
        type: STOREFRONTS_FETCH_ALL,
        payload: api.get('/storefronts'),
      });

      dispatch(this.fetchAllThumbnails(result));
      return result;
    };
  },

  loadAll() {
    return (dispatch, getState) => {
      const { storefronts } = getState();
      if (storefronts.all?.results) {
        return Promise.resolve(storefronts.all);
      }
      return dispatch(this.fetchAll());
    };
  },

  create(data) {
    return {
      type: STOREFRONTS_CREATE,
      payload: api.post('/storefronts', data),
    };
  },

  ensureAppInstalled(appId) {
    return async (dispatch, getState) => {
      const { apps: installedApps } = getState().client;

      const installed = installedApps.results.find(
        (installedApp) => installedApp.app_id === appId,
      );

      if (installed) {
        return installed;
      }

      const result = await dispatch(clientActions.installApp(appId));

      if (!result || result.errors) {
        throw new Error('Unable to install app');
      }

      return result;
    };
  },

  // `storefrontApp` is the app a theme runs on, when the merchant was already
  // shown which one would be installed
  createWithApp(app, data = {}, { storefrontApp: themeStorefrontApp } = {}) {
    return async (dispatch, getState) => {
      const installedApps = getState().client.apps;
      const apps = getState().apps.storefrontShops;
      const { theme } = app;

      try {
        if (app.type === 'theme') {
          if (!theme?.storefront?.app) {
            throw new Error('Theme does not have a storefront app defined');
          }

          const storefrontApp =
            themeStorefrontApp ||
            getThemeStorefrontApp(app, apps, installedApps);

          if (!storefrontApp) {
            throw new Error(
              'The storefront app was not found in available apps',
            );
          }

          await dispatch(this.ensureAppInstalled(storefrontApp.id));
          await dispatch(this.ensureAppInstalled(app.id));

          data.app_id = storefrontApp.id;
          data.theme_app_id = app.id;
        } else {
          await dispatch(this.ensureAppInstalled(app.id));
          data.app_id = app.id;
        }
      } catch (err) {
        console.error(err);
        return false;
      }

      return await dispatch(this.create(data));
    };
  },

  update(id, data) {
    return function (dispatch) {
      if (data.external?.custom_url) {
        data.external.custom_url = normalizeUrl(data.external.custom_url, {
          stripWWW: false,
        });
      }

      return dispatch({
        type: STOREFRONTS_UPDATE,
        payload: api.put(`/storefronts/${id}`, data),
      });
    };
  },

  getInstalledApps(storefront) {
    return function (_dispatch, getState) {
      const {
        client,
        apps: { deprecatedApps },
      } = getState();

      const { app_id, theme_app_id } = storefront;

      const storefrontApp =
        app_id &&
        client.apps.results.find((installed) => installed.app_id === app_id);

      const themeApp =
        theme_app_id &&
        client.apps.results.find(
          (installed) => installed.app_id === theme_app_id,
        );

      let deprecatedApp;
      if (app_id && !storefrontApp) {
        deprecatedApp = deprecatedApps?.results?.find(
          (app) => app.id === app_id,
        );
      }

      return { storefrontApp, themeApp, deprecatedApp };
    };
  },

  fetchThemeDetails(themeId) {
    return {
      type: STOREFRONTS_FETCH_THEME_DETAILS,
      payload: api.get(`/data/:themes/${themeId}`),
    };
  },

  fetchThemeConfig(themeId, file_path, branchId = null) {
    return {
      type: STOREFRONTS_FETCH_THEME_CONFIG,
      payload: api.get('/data/:themes:configs/:last', {
        parent_id: themeId,
        file_path,
        preview: true,
        fields:
          'date_created, date_updated, type, name, file, file_path, parent_id, version, hash, settings_hash',
        include: {
          file_data: FILE_DATA_INCLUDE_QUERY,
        },
      }),
      meta: { id: this.themeConfigsBranchId(themeId, branchId), file_path },
    };
  },

  createThemeConfig(data) {
    return (dispatch) => {
      return dispatch({
        type: STOREFRONTS_CREATE_THEME_CONFIG,
        payload: api.post('/data/:themes:configs', data).then((result) => {
          return dispatch(
            this.fetchThemeConfig(result.parent_id, result.file_path),
          );
        }),
      });
    };
  },

  updateThemeConfig(id, data) {
    return (dispatch) => {
      return dispatch({
        type: STOREFRONTS_UPDATE_THEME_CONFIG,
        payload: api.put(`/data/:themes:configs/${id}`, data).then((result) => {
          return dispatch(
            this.fetchThemeConfig(result.parent_id, result.file_path),
          );
        }),
      });
    };
  },

  deleteThemeConfig(id) {
    return {
      type: STOREFRONTS_DELETE_THEME_CONFIG,
      payload: api.delete(`/data/:themes:configs/${id}`),
    };
  },

  fetchThemeConfigs(themeId, branchId = null, query = undefined) {
    return {
      type: STOREFRONTS_FETCH_THEME_CONFIGS,
      payload: api.getAll('/data/:themes:configs', {
        parent_id: themeId,
        branch_id: branchId,
        preview: true,
        type: 'theme',
        fields:
          'date_created, date_updated, type, name, file, file_path, version, hash, settings_hash',
        sort: 'date_created asc',
        include: {
          file_data: FILE_DATA_INCLUDE_QUERY,
        },
        ...query,
      }),
      meta: { id: this.themeConfigsBranchId(themeId, branchId), query },
    };
  },

  loadThemeConfigs(themeId, branchId = null) {
    return (dispatch, getState) => {
      const { storefronts } = getState();
      const configsBranchId = this.themeConfigsBranchId(themeId, branchId);
      if (storefronts.themeConfigs[configsBranchId]) {
        return Promise.resolve(storefronts.themeConfigs[configsBranchId]);
      }
      if (storefronts.themeConfigs[themeId]) {
        return Promise.resolve(storefronts.themeConfigs[themeId]);
      }
      return dispatch(this.fetchThemeConfigs(themeId, branchId));
    };
  },

  fetchPrimaryThemeTemplates(storefront) {
    return async (dispatch) => {
      try {
        const SHOPIFY_CONFIG_DATA_FILE_PATH = 'theme/config/settings_data.json';
        const [themeConfigs, shopifyCompatibilityConfig] = await Promise.all([
          api.getAll('/data/:themes:configs', {
            parent_id: storefront.theme_id,
            branch_id: null,
            preview: true,
            type: 'theme',
            fields: 'file_path',
            $or: [
              { file_path: { $regex: '^theme/templates/' } },
              // To determine if it's a shopify compatible theme
              { file_path: SHOPIFY_CONFIG_DATA_FILE_PATH },
            ],
          }),
          dispatch(
            appActions.loadConfigFile(
              storefront.app_id,
              'shopify_compatibility.json',
            ),
          ),
        ]);

        const shopifyCompatibilityConfigData = JSON.parse(
          shopifyCompatibilityConfig,
        );

        const shopifyCompatible = themeConfigs?.results?.some(
          (config) => config.file_path === SHOPIFY_CONFIG_DATA_FILE_PATH,
        );

        const templateConfigs = themeConfigs?.results.filter(
          (config) => config.file_path !== SHOPIFY_CONFIG_DATA_FILE_PATH,
        );

        const storefrontApp = await dispatch(
          appActions.load(storefront.app_id),
        );

        /** @type {Record<string, ThemeTemplates | undefined>} */
        const themeTemplates = {};

        for (const [collection, id] of Object.entries(
          storefrontApp?.storefront?.theme?.template_collections || {},
        )) {
          const templateId =
            (shopifyCompatible &&
              shopifyCompatibilityConfigData?.page_types?.[id]) ||
            id;

          let templates = themeTemplates[collection];

          if (!templates) {
            templates = {
              templateId,
              list: [],
            };

            themeTemplates[collection] = templates;
          }

          const collectionTemplateConfigs = templateConfigs
            .map((config) =>
              config.file_path
                .replace(/^theme\/templates\//, '')
                .replace(/\.[^.]+$/, ''),
            )
            .filter((path) => path && path.startsWith(`${templateId}.`))
            .map((path) => path.replace(`${templateId}.`, ''))
            .filter(Boolean);

          templates.list.push(...collectionTemplateConfigs);
        }

        dispatch({
          type: STOREFRONTS_FETCH_PRIMARY_THEME_TEMPLATES,
          payload: themeTemplates,
        });
      } catch (err) {
        console.log(`Unable to load theme collection templates: ${err}`);
      }
    };
  },

  themeConfigsBranchId(id, branchId = null) {
    return branchId ? `${branchId}__${id}` : id;
  },

  fetchUpgradeConflicts(storefrontId, themeVersion) {
    return api.get(`/storefronts/${storefrontId}/conflicts`, {
      theme_version: themeVersion,
    });
  },

  // DEPRECATED: V1 storefronts
  updateVersion:
    ({ tag }) =>
    (dispatch, getState) => {
      const {
        storefronts: { storefront },
      } = getState();
      return dispatch({
        type: STOREFRONTS_UPDATE_VERSION,
        payload: api.put(`/storefronts/${storefront.id}`, {
          $update: tag,
        }),
        showLoading: false,
      });
    },

  // DEPRECATED: V1 storefronts
  cancelUpdate: () => (dispatch, getState) => {
    const {
      storefronts: { storefront },
    } = getState();

    return dispatch({
      type: STOREFRONTS_CANCEL_UPDATE,
      payload: api.put(`/storefronts/${storefront.id}`, {
        $update: false,
      }),
      showLoading: false,
    });
  },

  delete(id) {
    return {
      type: STOREFRONTS_DELETE,
      payload: api.delete(`/storefronts/${id}`),
    };
  },

  createAPIKey(id, data) {
    return {
      type: STOREFRONTS_CREATE_API_KEY,
      payload: api.post(`/storefronts/${id}/keys`, data),
    };
  },

  updateAPIKey(id, keyId, data) {
    return {
      type: STOREFRONTS_UPDATE_API_KEY,
      payload: api.put(`/storefronts/${id}/keys/${keyId}`, data),
    };
  },

  deleteAPIKey(id, keyId) {
    return {
      type: STOREFRONTS_DELETE_API_KEY,
      payload: api.delete(`/storefronts/${id}/keys/${keyId}`),
    };
  },

  fetchDomains() {
    return {
      type: STOREFRONTS_FETCH_DOMAINS,
      payload: api.get('/storefronts/domains'),
    };
  },

  createDomain(data) {
    return {
      type: STOREFRONTS_CREATE_DOMAIN,
      payload: api.post('/storefronts/domains', data),
    };
  },

  updateDomain(domainId, data) {
    return {
      type: STOREFRONTS_UPDATE_DOMAIN,
      payload: api.put(`/storefronts/domains/${domainId}`, data),
    };
  },

  verifyDomain(domainId) {
    return {
      type: STOREFRONTS_VERIFY_DOMAIN,
      payload: api.put(`/storefronts/domains/${domainId}/verify`),
    };
  },

  deleteDomain(domainId) {
    return {
      type: STOREFRONTS_DELETE_DOMAIN,
      payload: api.delete(`/storefronts/domains/${domainId}`),
    };
  },

  fetchEntriToken() {
    return {
      type: STOREFRONTS_FETCH_ENTRI_TOKEN,
      payload: api.get('/storefronts/entri-token'),
    };
  },

  fetchThumbnails(storefront) {
    return (dispatch) => {
      if (!storefront) {
        return;
      }

      // Only fetch if thumbnail updated date differs
      if (
        storefront.thumbnails?.success &&
        storefront.thumbnails?.date_updated
      ) {
        let dateUpdated = storefront.date_updated;
        if (storefront.theme) {
          dateUpdated = storefront.theme.date_updated;
        }
        if (
          storefront.thumbnails?.success &&
          storefront.thumbnails.date_updated >= dateUpdated
        ) {
          return;
        }
      } else if (
        storefront.thumbnails?.date_updated &&
        // check if date updated is at least 1 minute after last update
        moment().diff(moment(storefront.thumbnails.date_updated), 'minute') < 1
      ) {
        return;
      }

      dispatch({
        type: STOREFRONTS_FETCH_THUMBNAILS,
        payload: {},
        meta: { id: storefront.id, loading: true },
      });

      return dispatch({
        type: STOREFRONTS_FETCH_THUMBNAILS,
        payload: api.get(`/storefronts/${storefront.id}`, {
          $thumbnails: true,
        }),
        meta: { id: storefront.id },
        showLoading: false,
      });
    };
  },

  fetchAllThumbnails(all) {
    return (dispatch) => {
      for (const storefront of all?.results || []) {
        dispatch(this.fetchThumbnails(storefront));
      }
    };
  },

  fetchInfo() {
    return {
      type: STOREFRONTS_FETCH_INFO,
      payload: api.getStoreInfo(),
    };
  },

  postPassword(password) {
    return {
      type: STOREFRONTS_POST_PASSWORD,
      payload: api.postStorePassword(password),
    };
  },

  getTestCheckout(reset) {
    return {
      type: STOREFRONTS_GET_TEST_CHECKOUT,
      payload: api.get('/test-checkout', { reset }),
    };
  },

  fetchConfig(id, type) {
    return {
      type: STOREFRONTS_FETCH_CONFIG,
      payload: api.getLocalized(`/storefronts/${id}/configs/${type}`),
      meta: { id, type },
    };
  },

  updateConfig(id, type, values) {
    return {
      type: STOREFRONTS_UPDATE_CONFIG,
      payload: api.put(`/storefronts/${id}/configs/${type}`, values),
      meta: { id, type },
    };
  },

  fetchSettings(id) {
    return async (dispatch) => {
      const config = await dispatch(this.fetchConfig(id, 'settings'));
      // Undefined when the request failed
      return config === undefined ? undefined : config?.values || {};
    };
  },

  updateSettings(id, values) {
    return (dispatch) => {
      return dispatch(
        this.updateConfig(id, 'settings', {
          $set: {
            values,
          },
        }),
      );
    };
  },

  fetchMenus(id) {
    return async (dispatch) => {
      const config = await dispatch(this.fetchConfig(id, 'menus'));
      await populateMenuLookupValues(config?.values?.menus);
      return config?.values?.menus;
    };
  },

  updateMenu(id, values) {
    return async (dispatch, getState) => {
      const {
        storefronts: { configs },
      } = getState();

      const exMenus =
        (configs.menus ?? (await dispatch(this.fetchConfig(id, 'menus'))))
          ?.values?.menus || [];

      let menus = sortBy(
        [
          ...pull(
            [...exMenus],
            find(exMenus, (menu) => menu.id === values.id),
          ),
          {
            ...values,
          },
        ],
        (menu) => menu.name,
      );

      menus = depopulateMenuLookupValues(menus);

      return dispatch(
        this.updateConfig(id, 'menus', {
          $set: {
            values: { menus },
          },
        }),
      );
    };
  },

  deleteMenu(id, menuId) {
    return async (dispatch, getState) => {
      const {
        storefronts: { configs },
      } = getState();

      const exMenus =
        (configs.menus ?? (await dispatch(this.fetchConfig(id, 'menus'))))
          ?.values?.menus || [];

      let menus = pull(
        [...exMenus],
        find(exMenus, (menu) => menu.id === menuId),
      );

      menus = depopulateMenuLookupValues(menus);

      return dispatch(
        this.updateConfig(id, 'menus', {
          $set: {
            values: { menus },
          },
        }),
      );
    };
  },
};

/**
 * @typedef {object} ThemeTemplates
 * @property {string} templateId
 * @property {string[]} list
 */

export const initialState = {
  primary: null, // global main primary storefront
  storefront: null, // changes on page context
  all: {},
  info: null,
  testCheckout: null,
  loading: false,
  thumbnails: null,
  publishStatus: {},
  creatingQueue: {},
  fetchedPrimary: false,
  domains: null,

  // DEPRECATED: V1 storefronts
  configs: {},

  /**
   * V2 storefront themes
   * @type {Record<string, { count: number; results: object[] } | undefined>}
   */
  themeConfigs: {},

  /**
   * Theme templates for resolving alternate template IDs
   * @type {Record<string, ThemeTemplates | undefined>}
   */
  primaryThemeTemplates: {},
};

function setStorefrontConfig(state, action) {
  // Make sure config is being fetched for the current storefront
  if (state.storefront?.id && action.meta.id !== state.storefront?.id) {
    return state;
  }

  return {
    ...state,
    configs: { ...state.configs, [action.meta.type]: action.payload },
  };
}

export function reducer(state = initialState, action) {
  switch (action.type) {
    case 'RESET':
      return initialState;

    case STOREFRONTS_FETCH_PRIMARY:
      return {
        ...state,
        primary: storefrontProps(action.payload),
        fetchedPrimary: true,
      };

    case STOREFRONTS_FETCH_PRIMARY_THEME_TEMPLATES:
      return {
        ...state,
        primaryThemeTemplates: action.payload,
      };

    case STOREFRONTS_FETCH: {
      // Clear configs if a new storefront is being loaded
      const configs =
        !state.storefront?.id || state.storefront?.id === action.payload?.id
          ? state.configs
          : {};

      const storefront = storefrontProps(action.payload);

      return {
        ...state,
        storefront,
        configs,
        ...(action.payload?.primary ? { primary: storefront } : {}),
      };
    }

    case STOREFRONTS_FETCH_ALL:
      return {
        ...state,
        all: {
          ...action.payload,
          results: action.payload?.results?.map((storefront) =>
            storefrontProps(storefront),
          ),
        },
      };

    case STOREFRONTS_FETCH_INFO:
      return {
        ...state,
        info: action.payload,
      };

    case STOREFRONTS_UPDATE: {
      if (action.payload?.errors) {
        return state;
      }

      const storefront = storefrontProps(action.payload);

      return {
        ...state,
        ...(state.storefront?.id === action.payload.id
          ? {
              ...storefront,
              ...(state.storefront.theme
                ? { theme: state.storefront.theme }
                : undefined),
            }
          : undefined),
        ...(action.payload.primary ? { primary: storefront } : undefined),
      };
    }

    case STOREFRONTS_UPDATE_CONFIG: {
      if (action.payload?.errors) {
        return state;
      }

      return setStorefrontConfig(state, action);
    }

    case STOREFRONTS_FETCH_CONFIG:
      return setStorefrontConfig(state, action);

    case STOREFRONTS_LOADING:
      return {
        ...state,
        loading: action.payload,
      };

    case STOREFRONTS_GET_TEST_CHECKOUT:
      return {
        ...state,
        testCheckout: action.payload,
      };

    case USER_FETCH:
      if (action.payload && action.payload.test_checkout_id) {
        return {
          ...state,
          testCheckout: {
            id: action.payload.test_checkout_id,
          },
        };
      }
      return state;

    case STOREFRONTS_UPDATE_VERSION:
      return {
        ...state,
        publishStatus: action.payload,
      };

    case STOREFRONTS_FETCH_DOMAINS:
      return {
        ...state,
        domains: action.payload,
      };

    case STOREFRONTS_FETCH_THUMBNAILS: {
      const { id, loading } = action.meta;
      const { thumbnails = {} } = action.payload;

      let { all } = state;
      if (all?.results) {
        const record = all.results.find((storefront) => storefront.id === id);
        if (record) {
          record.thumbnails = {
            ...(record.thumbnails || undefined),
            ...thumbnails,
            loading,
          };
          all = { ...all };
        }
      }

      let { primary } = state;
      if (primary?.id === id) {
        primary = {
          ...primary,
          thumbnails: {
            ...(primary.thumbnails || undefined),
            ...thumbnails,
            loading,
          },
        };
      }

      let { storefront } = state;
      if (storefront?.id === id) {
        storefront = {
          ...storefront,
          thumbnails: {
            ...(storefront.thumbnails || undefined),
            ...thumbnails,
            loading,
          },
        };
      }

      return {
        ...state,
        all,
        primary,
        storefront,
      };
    }

    case STOREFRONTS_FETCH_THEME_CONFIGS: {
      // Handle special requests
      if (action.meta.query?.id) {
        const configs = state.themeConfigs[action.meta.id];

        if (configs) {
          const themeConfigs = { ...configs, results: [...configs.results] };
          const configIds = convertQueryParamToSet(action.meta.query.id);

          // Create a map of existing config file paths to their indices
          /** @type {Map<string, number>} */
          const indexMap = themeConfigs.results.reduce((map, config, index) => {
            if (configIds.has(config.id)) {
              map.set(config.id, index);
            }

            return map;
          }, new Map());

          // Replace existing configs with the new ones.
          for (const config of action.payload.results) {
            const index = indexMap.get(config.id);

            if (typeof index === 'number') {
              // Replace the config if it is in the redux store
              themeConfigs.results[index] = config;
              indexMap.delete(config.id);
            } else {
              // Add config if it is not in redux store
              themeConfigs.results.push(config);
              themeConfigs.count += 1;
            }
          }

          // Delete any remaining configs that were not replaced.
          // Deletion must occur in reverse order so that indexes remain valid.
          Array.from(indexMap.values())
            .reverse()
            .forEach((index) => {
              themeConfigs.results.splice(index, 1);
            });

          themeConfigs.count -= indexMap.size;

          return {
            ...state,
            themeConfigs: {
              ...state.themeConfigs,
              [action.meta.id]: themeConfigs,
            },
          };
        }
      }

      // Process typical requests
      return {
        ...state,
        themeConfigs: {
          ...state.themeConfigs,
          [action.meta.id]: action.payload,
        },
      };
    }

    case STOREFRONTS_FETCH_THEME_CONFIG: {
      const configs = state.themeConfigs[action.meta.id];

      if (configs) {
        const themeConfigs = { ...configs, results: [...configs.results] };
        const targetFilePath = action.meta.file_path;

        const index = configs.results.findIndex(
          (config) => config.file_path === targetFilePath,
        );

        if (index !== -1) {
          themeConfigs.results[index] = action.payload;
        } else {
          themeConfigs.results.push(action.payload);
          themeConfigs.count += 1;
        }

        return {
          ...state,
          themeConfigs: {
            ...state.themeConfigs,
            [action.meta.id]: themeConfigs,
          },
        };
      }

      return state;
    }

    case STOREFRONTS_CREATE_THEME_CONFIG:
    case STOREFRONTS_UPDATE_THEME_CONFIG: {
      const { payload } = action;
      const configs = state.themeConfigs[payload.parent_id];

      if (configs) {
        const index = configs.results.findIndex(
          (config) => config.id === payload.id,
        );

        if (index !== -1) {
          const themeConfigs = { ...configs, results: [...configs.results] };
          themeConfigs.results[index] = payload;

          return {
            ...state,
            themeConfigs: {
              ...state.themeConfigs,
              [payload.parent_id]: themeConfigs,
            },
          };
        }
      }

      return state;
    }

    case STOREFRONTS_DELETE_THEME_CONFIG: {
      const { payload } = action;
      const configs = state.themeConfigs[payload.parent_id];

      if (configs) {
        const index = configs.results.findIndex(
          (config) => config.id === payload.id,
        );

        if (index !== -1) {
          const themeConfigs = { ...configs, results: [...configs.results] };
          themeConfigs.results.splice(index, 1);

          return {
            ...state,
            themeConfigs: {
              ...state.themeConfigs,
              [payload.parent_id]: themeConfigs,
            },
          };
        }
      }

      return state;
    }

    default:
      return state;
  }
}

export function storefrontProps(storefront) {
  if (!storefront) {
    return null;
  }

  const isExternal = Boolean(
    storefront.external === true || storefront.external?.custom,
  );

  return {
    ...storefront,
    // Adapt external props from deprecated fields
    external: isExternal
      ? {
          custom: true,
          custom_url:
            storefront.external_url || storefront.external?.custom_url,
        }
      : undefined,
  };
}

export function generatePasswordAndCookie(storefrontSlugId) {
  const password = generatePassword.generate({
    length: 8,
    numbers: false,
    symbols: false,
    excludeSimilarCharacters: true,
  });

  const domain = new URL(
    process.env.CLIENT_URL.replace('CLIENT_ID', storefrontSlugId),
  ).host.replace(/:.*/, '');

  cookies.set('_swell_password', password, {
    path: '/',
    maxAge: COOKIE_MAX_AGE,
    domain,
    // client cookies cannot be httponly
    httpOnly: false,
    sameSite: 'lax',
    secure: process.env.NODE_ENV !== 'development',
  });

  return password;
}

function convertQueryParamToSet(param) {
  const set = new Set();

  switch (typeof param) {
    case 'string':
      set.add(param);
      break;

    case 'object': {
      if (param === null) {
        break;
      }

      if (Array.isArray(param)) {
        for (const item of param) {
          set.add(item);
        }

        break;
      }

      if (Object.hasOwn(param, '$in')) {
        for (const item of param.$in) {
          set.add(item);
        }

        break;
      }

      break;
    }

    default:
      break;
  }

  return set;
}

export default actions;
